<template>
  <v-container fluid class="pa-7">
    <header-component class="pb-4" :title="$vuetify.lang.t('$vuetify.navigation.customers')"> </header-component>

    <v-row class="pt-5">
      <v-col cols="lg-4" offset-lg="4">
        <form class="d-flex justify-stretch gap-1 mb-6" @submit.prevent="search">
          <v-text-field
            :label="$vuetify.lang.t('$vuetify.search.idOrEmail')"
            outlined
            single-line
            hide-details
            v-model="searchQuery"
            clearable
            :disabled="filterActive"
          />
          <v-btn height="auto" color="success" type="submit" :disabled="isLoading">
            <v-icon v-if="!isLoading">$vuetify.icons.check</v-icon>
            <v-progress-circular v-else indeterminate color="white" />
          </v-btn>
        </form>

        <div class="d-flex mt-4 w-100">
          <QrCodeReaderComponent @decode="decode($event)" :is-order="false" width="100%" height="56" class="w-100" />
        </div>

        <template v-if="!searchedCustomer">
          <p class="text-center py-10 mb-0">{{ $vuetify.lang.t('$vuetify.customers.or') }}</p>
          <router-link :to="{ name: 'customers.create' }">
            <v-btn v-bind="$attrs" color="primary" dark class="w-100" height="56">
              <v-icon color="white" class="mr-4">$vuetify.icons.plus</v-icon>
              {{ $vuetify.lang.t('$vuetify.customers.createCustomer') }}
            </v-btn>
          </router-link>
        </template>

        <template v-else>
          <div class="mt-10">
            <div class="mb-5">
              <div class="d-flex align-center justify-space-between">
                <span class="d-block" :style="{ color: $vuetify.theme.themes.light.primary.base, fontSize: '2rem' }">
                  {{ searchedCustomer.firstName }} {{ searchedCustomer.lastName }}
                </span>
                <v-btn @click="backToStart" color="primary" dark height="36">
                  <v-icon color="white" class="mr-4">$vuetify.icons.arrowLeft</v-icon>
                  {{ $vuetify.lang.t('$vuetify.customers.back') }}
                </v-btn>
              </div>

              <span class="d-block">
                {{ searchedCustomer.email }}
              </span>
              <span class="d-block">
                ID:
                {{ searchedCustomer.id }}
              </span>
              <div class="d-flex justify-space-between mt-3" v-for="card in customerLoyaltyCards" :key="card.id">
                <span>
                  Karta ID:
                  {{ card.code }}
                </span>
                <button @click="removeLoyaltyCard(card.id, card.code)" class="remove-card">X</button>
              </div>
            </div>
          </div>

          <form class="d-flex justify-stretch gap-1 mb-6" @submit.prevent="addLoyaltyCard">
            <v-text-field
              :label="$vuetify.lang.t('$vuetify.customers.addLoyaltyCard')"
              outlined
              single-line
              hide-details
              v-model="loyaltyCardId"
              clearable
              :disabled="filterActive"
            />
            <v-btn height="auto" color="success" type="submit" :disabled="isLoading">
              <v-icon v-if="!isLoading">$vuetify.icons.check</v-icon>
              <v-progress-circular v-else indeterminate color="white" />
            </v-btn>
          </form>

          <div class="d-flex mt-4 w-100">
            <QrCodeReaderComponent
              @decode="decodeCard($event)"
              :is-order="false"
              width="100%"
              height="56"
              class="w-100"
            />
          </div>

          <form class="d-flex justify-stretch gap-1 mt-8 mb-6" @submit.prevent="addCustomerComment">
            <v-text-field
              :label="$vuetify.lang.t('$vuetify.customers.addComment')"
              outlined
              single-line
              hide-details
              v-model="customerComment"
              clearable
              :disabled="filterActive"
            />
            <v-btn height="auto" color="success" type="submit" :disabled="isLoading">
              <v-icon v-if="!isLoading">$vuetify.icons.check</v-icon>
              <v-progress-circular v-else indeterminate color="white" />
            </v-btn>
          </form>

          <v-timeline dense clipped>
            <v-slide-x-transition group>
              <v-timeline-item
                v-for="comment in customerAllComments"
                :key="comment.id"
                class="mb-4"
                color="black"
                small
              >
                <v-row justify="space-between" align="center">
                  <v-col cols="7">{{ comment.comment }}</v-col>
                  <v-col class="d-flex justify-end text-right" cols="5">
                    <div>
                      <div v-if="comment && comment.userName" class="lighten">
                        {{ comment.userName }}
                      </div>
                      <div class="note">{{ formatDate(comment.createdAt) }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-timeline-item>
            </v-slide-x-transition>
          </v-timeline>
        </template>
      </v-col>
    </v-row>

    <!--  A prompt to create a new customer if the search query is a non-existing customer ID  -->
    <!--  In case of an email, the prompt is not shown  -->
    <v-dialog :value="isCreateCustomerPromptOpen" @click:outside="isCreateCustomerPromptOpen = false" max-width="400">
      <v-card>
        <v-card-title class="headline"> {{ $vuetify.lang.t('$vuetify.customers.createCustomer') }}? </v-card-title>

        <v-card-text>
          {{ $vuetify.lang.t('$vuetify.messages.customerWithIdDoesNotExistCreatePrompt', this.searchQuery) }}
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-btn color="grey lighten-2" @click="isCreateCustomerPromptOpen = false">
            {{ $vuetify.lang.t('$vuetify.basic.no') }}
          </v-btn>
          <v-spacer />
          <router-link :to="{ name: 'customers.create', params: { id: searchQuery } }">
            <v-btn color="success">
              {{ $vuetify.lang.t('$vuetify.basic.yes') }}
            </v-btn>
          </router-link>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import QrCodeReaderComponent from '../../components/global/QrCodeReaderComponent.vue';
import { CustomersApiService } from '../../common/services/customers.api.service';
import { LoyaltyProgramCardsApiService } from '../../common/services/loyalty-program-cards.api.service';
import { CustomersCommentsApiService } from '../../common/services/customers-comments.api.service';
import { isEmail } from '../../common/helpers/form';
import { formatDateTime } from '../../common/helpers/date';

export default {
  name: 'OrdersList',

  components: {
    HeaderComponent,
    QrCodeReaderComponent,
  },

  data() {
    return {
      searchTimer: null,
      searchQuery: null,
      searchPending: false,
      filterActive: false,
      options: { sortDesc: [false], sortBy: ['created_at'] },
      searchedCustomer: null,
      createPending: false,
      isLoading: false,
      isCreateCustomerPromptOpen: false,
      loyaltyCardId: null,
      customerLoyaltyCards: [],
      customerAllComments: null,
      customerComment: null,
    };
  },

  computed: {
    ...mapGetters({
      items: 'orders/getAllOrders',
      total: 'orders/getTotal',
      status: 'orders/getStatus',
      lastChange: 'orders/getLastChange',
      formLanguage: 'languages/formLanguage',
      storeData: 'users/getStoreData',
      userRoles: 'auth/getUserRoles',
      user: 'auth/getUser',
    }),

    isSearchQueryEmail() {
      return isEmail(this.searchQuery);
    },

    pagination() {
      return {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        pageStart: this.options.page * this.options.itemsPerPage - this.options.itemsPerPage,
        pageStop: this.options.page * this.options.itemsPerPage,
        pageCount: Math.ceil(this.total / this.options.itemsPerPage),
        itemsLength: this.$store.state.orders.total,
      };
    },

    lastChangeText() {
      if (!this.lastChange) {
        return '';
      }

      return this.$vuetify.lang.t(
        '$vuetify.messages.latestOrderChange',
        this.lastChange.orderId,
        this.lastChange.itemsCount,
        this.lastChange.totalTaxedPrice
      );
    },

    activeCurrency() {
      return this.$vuetify.lang.t(
        `$vuetify.currency.country.${
          this.storeData && this.storeData.countryId ? this.storeData.countryId.toUpperCase() : 'CZE'
        }`
      );
    },
  },

  methods: {
    ...mapActions({}),
    decode(e) {
      setTimeout(() => {
        this.searchQuery = e;
        this.$forceUpdate();
      }, 100);
    },

    decodeCard(e) {
      setTimeout(() => {
        this.loyaltyCardId = e;
        this.$forceUpdate();
      }, 100);
    },

    formatDate(date) {
      return formatDateTime(date);
    },

    async search() {
      this.searchedCustomer = null;

      if (!this.searchQuery) {
        return;
      }

      this.searchQuery = this.searchQuery.toString().trim();

      this.isLoading = true;

      if (!this.isSearchQueryEmail) {
        try {
          const card = await this.searchLoyaltyCardByCode(this.searchQuery);

          if (card && card.customerId) {
            this.searchCustomer(card.customerId);
          } else {
            this.searchCustomer(this.searchQuery);
          }
        } catch (error) {
          this.$alertError(error.getFirstValidationErrorMessage() || error.getMessage());
        }
      } else {
        this.searchCustomer(this.searchQuery);
      }
    },

    async searchLoyaltyCardByCode(cardCode) {
      return (
        await LoyaltyProgramCardsApiService.getAll(builder => {
          builder.whereEquals('code', cardCode);
        })
      ).getItems()[0];
    },

    searchCustomer(searchQuery) {
      CustomersApiService.getAll(builder => {
        if (!/[a-zA-Z]/.test(searchQuery) && !this.isSearchQueryEmail) {
          builder.whereEquals('id', parseInt(searchQuery));
        } else {
          builder.whereEquals('email', searchQuery);
        }
      })
        .then(response => {
          const responseItems = response.getItems();

          if (!responseItems.length && searchQuery) {
            this.openCreateCustomerPrompt();
            this.$alertError(this.$vuetify.lang.t('$vuetify.messages.customerNotFound'));
          }

          if (responseItems.length) {
            this.$alertSuccess(this.$vuetify.lang.t('$vuetify.messages.customerFound'));
          }

          this.searchedCustomer = responseItems[0];

          this.getCustomerLoyaltyCards();

          this.getCustomerComments();

          this.isLoading = false;
        })
        .catch(() => {
          if (searchQuery) {
            this.$alertError(this.$vuetify.lang.t('$vuetify.messages.customerNotFound'));
            this.isLoading = false;
            this.openCreateCustomerPrompt();
          }
        });
    },

    getCustomerLoyaltyCards() {
      LoyaltyProgramCardsApiService.getAll(builder => {
        builder.whereEquals('customer_id', this.searchedCustomer.id);
      }).then(response => {
        this.customerLoyaltyCards = response.getItems();
      });
    },

    async addLoyaltyCard() {
      try {
        const card = await this.searchLoyaltyCardByCode(this.loyaltyCardId);

        if (!card) {
          this.$alertError(this.$vuetify.lang.t('$vuetify.messages.cardNotExist'));

          return;
        }

        if (card.customerId) {
          this.$alertError(this.$vuetify.lang.t('$vuetify.messages.cardAlreadyUsed'));

          return;
        }

        await LoyaltyProgramCardsApiService.patch(card.id, {
          customer_id: this.searchedCustomer.id,
        });

        this.customerComment =
          this.$vuetify.lang.t('$vuetify.customers.autoComments.card') +
          card.code +
          this.$vuetify.lang.t('$vuetify.customers.autoComments.added');

        await this.addCustomerComment();

        this.getCustomerLoyaltyCards();

        this.loyaltyCardId = '';
      } catch (error) {
        this.$alertError(error.getFirstValidationErrorMessage() || error.getMessage());
      }
    },

    async removeLoyaltyCard(cardId, cardCode) {
      try {
        await LoyaltyProgramCardsApiService.patch(cardId, {
          customer_id: '',
        });

        this.customerComment =
          this.$vuetify.lang.t('$vuetify.customers.autoComments.card') +
          cardCode +
          this.$vuetify.lang.t('$vuetify.customers.autoComments.removed');

        await this.addCustomerComment();

        this.customerLoyaltyCards = this.customerLoyaltyCards.filter(card => card.id !== cardId);
      } catch (error) {
        this.$alertError(error.getFirstValidationErrorMessage() || error.getMessage());
      }
    },

    /**
     * Opens the prompt with a question whether to create a new customer
     * if all the checks pass.
     * Automatically checks whether the `id` is a valid one and can be
     * used to create a new customer. (doesn't open the modal otherwise)
     */
    openCreateCustomerPrompt() {
      // skip if the search query is email (not an id)
      if (this.isSearchQueryEmail) return;
      // skip if the search query is not digit only (id cannot have other characters)
      if (!this.searchQuery || !/^\d+$/.test(this.searchQuery)) return;

      // open the prompt if all checks passed
      this.isCreateCustomerPromptOpen = true;
    },

    async addCustomerComment() {
      try {
        await CustomersCommentsApiService.post(this.searchedCustomer.id, {
          comment: this.customerComment,
        });

        this.customerComment = null;

        this.getCustomerComments();
      } catch (error) {
        this.$alertError(error.getFirstValidationErrorMessage() || error.getMessage());
      }
    },

    getCustomerComments() {
      CustomersCommentsApiService.getAll(this.searchedCustomer.id, builder => {
        builder.embed(['user_name']);
      })
        .then(response => {
          this.customerAllComments = response.getItems();
        })
        .catch(() => {
          this.$alertError(this.$vuetify.lang.t('$vuetify.messages.commentNotAdded'));
        });
    },

    backToStart() {
      this.searchQuery = null
      this.searchedCustomer = null
    },
  },

  created() {
    this.searchQuery = this.$route.params.customerId;
    this.search();
  },
};
</script>

<style scoped>
.info-text {
  display: block;
  text-align: center;
  padding: 1rem;
  margin-bottom: 2rem;
  background: #1cad1c;
  color: white;
  border-radius: 4px;
}

.remove-card {
  margin-left: 3rem;
  color: red;
  padding: 0 1rem;
}
</style>
